import { getOrExtractImageInfo, getImageInfo, updateImageInfo } from "@/store/processing/image-info"
import { store } from "@/store/store"
import { ImageInfoRecord, PhotoMetadataRecord, PhotoRecord, StyleInfoRecord } from "@/lib/catalog"
import {
	BiasType,
	getTemperatureBias,
	refCurrentAdjustmentsId,
	saturationBiasBW,
	getUseLRParamsForJpegReference,
} from "@/lib/ai/calibration/ai-calibration-feature"
import { isEmpty, isPresetAlmostEqual } from "@/lib/ai/calibration/ai-calibration-utils"
import {
	ClassifiedLocalAdjustments,
	getClassifiedLocalAdjustments,
	getLocalAdjustmentsBiases,
	isPresetMaskUpdated,
} from "@/lib/ai/calibration/ai-calibration-mask"
import { debounce } from "@/utils/debounce"
import { AbortError } from "@/utils/error"
import { derivePresetWithUserAdjustmentsForAI } from "@/store/store-presets"
import { AI } from "@/lib/ai/ai"
import { getPhotoMetadata, photosAtom, updatePhoto } from "@/store/photo/photos-state"
import { currentStyleAtom } from "@/store/style/style-state"
import { updateAIStyle } from "@/store/style/update-style"
import { holdAIStyleScheduling } from "./ai-style-performance"
import { fetchCurrentAdjustmentsFromHistory, fetchPresetIDsFromHistory } from "../store-history"
import equal from "fast-deep-equal"
import { getFileForPhoto } from "../files-state"
import { makeApprovedPhotoKeyNeighbor } from "@/lib/ai/ai-cluster"
import { isCalibrationAtom, projectAtom } from "@/store/project/projects-state"
import { Adjustments, ColorProfileName, MaskGroup } from "@/core/types"
import { fetchCurrentPreview, flowFeatureExists, storeFlowFeature } from "@/storage/indexeddb"
import {
	aggregatePresets,
	isParamsEmpty,
	isMainParamsEqual,
	isTruePreset,
	transformPresetMaskForPaste,
} from "./processing-utils"
import { transformLocalAdjustments } from "@/utils/math"
import { vec2 } from "gl-matrix"
import { logAiInfo } from "@/lib/ai/utils"
import { DefaultExcludedAdjustmentKeys, getDefaultAdjustments } from "@/core/adjustments"
import { hasLocalizedChanges } from "@/lib/ai/calibration/ai-calibration-keyadj"
import { autoStraightenAtom } from "../settings-state"
import { PhotoId } from "../undoable-state"
import { PresetItem } from "../presets/presets-service"
import { WhiteBalance } from "@/core/photo"
import { PreviewSize } from "../previews/previews-types"

export async function approveReferenceTask(photo: PhotoRecord, completionHandler?: () => void) {
	if (photo.kind === "reference") {
		throw new AbortError("Already a reference photo")
	}

	// update the status before the slow async function for immediate UI updates
	updatePhoto(photo.id, { kind: "reference" })

	try {
		await AI.lockEditor()
		await approveReference(photo)
	} finally {
		AI.unlockEditor()
	}

	completionHandler?.()
}

/**
 * Processes a photo as an AI style reference and populates its ImageInfoRecord.
 *
 * **Algorithm routing** (the most important decision made here):
 * - Preset has NO local adjustments AND no user LUT → **new algorithm** (neural color match):
 *   Extracts `colorMatchReference` features + `referenceWebpData256x256` (256×256 JPEG thumbnail).
 *   Downstream code detects this path via `imageInfo.colorMatchReference !== null`.
 * - Preset HAS local adjustments OR user LUT → **old algorithm** (rule-based preset + auto + bias):
 *   Sets `colorMatchReference = null`. Computes `auto = calculateAutoParams(preset)`,
 *   then stores `bias[key] = user_edit[key] − auto[key]` in `imageInfo.biases`.
 *   Neural color match cannot transfer local masks or opaque LUTs, so the old path preserves them.
 *
 * Also triggers optical flow feature extraction if the reference has localized changes
 * (crop, healing, perspective) that may need to propagate to cluster members via alignment.
 *
 * @param isExplicit - If false, computes imageInfo but does not persist it (only for implicit references in publish style flow).
 */
export async function approveReference(
	photo: PhotoRecord,
	isExplicit: boolean = true,
): Promise<ImageInfoRecord> {
	const file = getFileForPhoto(photo)
	const metadata = getPhotoMetadata(photo.id)

	if (!file) {
		throw "Cannot find file specified"
	}

	const photos = store.get(photosAtom)
	const approvedPhotoIds = Object.values(photos)
		.filter((photo) => photo.kind == "reference")
		.map((photo) => photo.id)
	const style = store.get(currentStyleAtom)

	const imageInfoOriginal = await getOrExtractImageInfo(photo) // original image info stored in catalog
	const imageInfo: ImageInfoRecord = { ...imageInfoOriginal } // temp copy that will not be stored if isExplicit is false
	const userAdjustments = await fetchCurrentAdjustmentsFromHistory(photo)
	const defaultAdjustments = getDefaultAdjustments(photo)

	const presetIds = await fetchPresetIDsFromHistory(photo)
	const presetAdjustmentsMap = imageInfo?.presetAdjustmentsMap ?? {}
	const aggregatedPreset = await aggregatePresets({
		presetIds,
		style,
		presetAdjustmentsMap,
		defaultAdjustments,
	})
	let presetId = aggregatedPreset.aggregatedId

	// Derive preset from userAdjustments unless it's a true preset
	// This is a heuristic decision: we assume a true preset is more robust and versatile and represents the color style consistently, so that directly using it is better than deriving a new preset from userAdjustments
	if (presetId && !isTruePreset(presetId)) {
		presetId = null
	}
	let presetAdjustments: Partial<Adjustments> | undefined = presetId
		? aggregatedPreset.aggregatedAdjustments
		: undefined

	// ─── Algorithm routing ────────────────────────────────────────────────────────
	// NEW algorithm (neural color match): no preset, or preset has no local masks.
	// → Extracts colorMatchReference features + a 256×256 thumbnail for downstream color matching.
	//   Only geometric transforms (crop, straighten, perspective) are stored as biases here;
	//   the full color grade is handled by the neural LUT path, not by bias deltas.
	//   User LUTs in presets are supported via CPU renderer.
	//
	// OLD algorithm (preset + auto + bias): preset has local adjustments.
	// → Neural color match can't transfer opaque masks, so we fall back to:
	//   bias[key] = user_edit[key] − auto_computed[key], stored on the reference for later blending.
	// ─────────────────────────────────────────────────────────────────────────────
	if (!presetId || (presetAdjustments?.localAdjustments?.length ?? 0) === 0) {
		logAiInfo("[approveReference] using current marked ref image for color match")

		//Try fetching from indexeddb preview to avoid AI.loadImage to be faster
		const afterPreview = (
			await fetchCurrentPreview({
				projectId: store.get(projectAtom)?.id ?? "",
				photo: photo,
				size: PreviewSize.medium,
			})
		)?.data
		// This is 1024x, slightly smaller than 1600x that was exported from ai-worker, but acceptable
		const afterBitmap = afterPreview ? await createImageBitmap(afterPreview) : undefined

		const asShotWB =
			imageInfoOriginal.features.meta.init_temp && imageInfoOriginal.features.meta.init_tint
				? {
						asShotTemperature: imageInfoOriginal.features.meta.init_temp,
						asShotTint: imageInfoOriginal.features.meta.init_tint,
					}
				: (undefined as WhiteBalance | undefined)

		const hasPositionChanges = DefaultExcludedAdjustmentKeys.some(
			(key) => !equal(userAdjustments[key], defaultAdjustments[key]),
		)
		const { referenceColorMatchFeatures, referenceWebpData256x256 } =
			await AI.getReferenceColorMatchFeaturesAndSetCache({
				imageBitmap: afterBitmap,
				faceBoxCoords: hasPositionChanges ? undefined : imageInfo?.features.faceBoxCoords,
				userAdjustments,
				imageID: imageInfo.imageId,
				returnWebpData: true,
				setCache: false,
				asShotWB,
				...(afterBitmap ? {} : { file, metadata }),
			})

		imageInfo.colorMatchReference = referenceColorMatchFeatures
		imageInfo.referenceWebpData256x256 = referenceWebpData256x256
		imageInfo.referenceWebpData256x256Url = null

		if (!presetId || !presetAdjustments) {
			const res = getPresetFromUserAdjustments({
				userAdjustments,
				defaultAdjustments,
				approvedPhotoIds,
				style,
				presetLocalAdjustments: [],
				appliedPresetId: aggregatedPreset.aggregatedId ?? undefined,
			})
			presetId = res.presetId
			presetAdjustments = res.presetAdjustments
			imageInfo.colorMatchReference.presetIntensity = res.presetIntensity
		}

		if (presetAdjustments && style?.presets[presetId] == undefined) {
			presetAdjustmentsMap[presetId] = { ...presetAdjustments }
		}

		// Add biases for non color adjustments + local masks for transfering keyAdjustments
		const biases: Partial<BiasType> = {}
		for (const key of DefaultExcludedAdjustmentKeys) {
			if (["crop", "cropStraighten", "perspectiveVertical"].includes(key)) {
				if (!equal(imageInfo?.features.autoCorrection[key], userAdjustments[key])) {
					;(biases[key] as any) = userAdjustments[key]
				}
			} else if (
				userAdjustments[key] != undefined &&
				!equal(defaultAdjustments[key], userAdjustments[key])
			) {
				;(biases[key] as any) = userAdjustments[key]
			}
		}
		if (userAdjustments.localAdjustments && userAdjustments.localAdjustments.length > 0) {
			biases.localAdjustmentsManual = userAdjustments.localAdjustments
		}

		const aiAdjustmentsMap = imageInfo.aiAdjustmentsMap ?? {}
		aiAdjustmentsMap[refCurrentAdjustmentsId] = userAdjustments

		const prevKeyID = imageInfo?.keyNeighborId
		const updatedInfo: ImageInfoRecord = {
			...imageInfo,
			biases,
			keyNeighborId: photo.id,
			presetId,
			nearestReferences: null,
			aiAdjustmentsMap,
			presetAdjustmentsMap,
		}

		if (isExplicit) {
			makeApprovedPhotoKeyNeighbor(updatedInfo, prevKeyID, false)

			await storeFlowFeatureIfNeeded({ userAdjustments, imageInfo, file, metadata })

			updateImageInfo(updatedInfo)
		}

		logAiInfo("[approveReference] using new color match for", photo.id, {
			referenceColorMatchFeatures,
			presetId,
			userAdjustments,
			updatedInfo,
			isExplicit,
		})

		return updatedInfo
	} else {
		imageInfo.colorMatchReference = null
		imageInfo.referenceWebpData256x256 = null
		imageInfo.referenceWebpData256x256Url = null
	}

	// ─── OLD algorithm: preset + auto + bias ─────────────────────────────────────

	let aiAdjustments: Partial<Adjustments> = presetId
		? (imageInfo?.aiAdjustmentsMap[presetId] ?? {})
		: {}

	// Transform user local mask to on-screen coord for copy
	const originalLocalAdjustments = structuredClone(userAdjustments.localAdjustments)
	if (originalLocalAdjustments) {
		const initialOrientation = imageInfo.features.initialOrientation ?? 0
		const [w, h] = imageInfo.features.imageSize ?? [400, 300] // oriented
		const originalImageSize: vec2 = Math.abs(initialOrientation) % 2 ? [h, w] : [w, h]
		const onScreenLocalAdjustments = transformLocalAdjustments(
			originalLocalAdjustments,
			userAdjustments,
			originalImageSize,
			initialOrientation,
			true,
		)
		userAdjustments.localAdjustments = onScreenLocalAdjustments
	}

	const presetMaskInfo = isPresetMaskUpdated(
		userAdjustments.localAdjustments,
		presetAdjustments?.localAdjustments,
	)
	logAiInfo("[approveReference] using old algorithm for", photo.id, {
		imageInfo,
		presetId,
		userAdjustments,
		presetAdjustments,
		presetMaskInfo,
		isExplicit,
	})

	// If no preset applied, derive a new preset with user adjustments
	// or if user deleted/modified mask(s) from preset, we also need to create new preset here
	if (!presetId || presetMaskInfo.updated) {
		const res = getPresetFromUserAdjustments({
			userAdjustments,
			defaultAdjustments,
			approvedPhotoIds,
			style,
			presetLocalAdjustments: presetMaskInfo.keptLocalAdjmts,
		})
		presetId = res.presetId
		presetAdjustments = res.presetAdjustments

		aiAdjustments = imageInfo.aiAdjustmentsMap[presetId] ?? {}
	}

	// Here we store presetAdjustments with aggregated presetId-with-strength to imageInfo.presetAdjustmentsMap;
	// when applying preset, we just store presetId-without-strength to imageInfo.presetAdjustmentsMap if not already exists in style.presets
	if (presetAdjustments && style?.presets[presetId] == undefined) {
		presetAdjustmentsMap[presetId] = { ...presetAdjustments }
	}

	// check if there is any manual local adjustments that's not in preset -> need recompute global auto
	let hasManualLocalAdj = false
	const classifiedLocals = getClassifiedLocalAdjustments(
		userAdjustments.localAdjustments,
		presetAdjustments?.localAdjustments,
	)
	if (classifiedLocals) {
		if (classifiedLocals.manual.length + classifiedLocals.modifiedPreset.length > 0) {
			// has manual local adjustments -> add it to preset adjustments for recomputing global auto correctly
			presetAdjustments = {
				...presetAdjustments,
				localAdjustments: userAdjustments.localAdjustments,
			}
			hasManualLocalAdj = true
		}
	}

	const transformedPresetAdjustments = transformPresetMaskForPaste(
		imageInfo,
		presetAdjustments ?? {},
		defaultAdjustments,
	)

	// Need compute AutoParams if aiAdjustments is empty (normally only for derived preset); or need recompute due to local
	if (isEmpty(aiAdjustments) || hasManualLocalAdj) {
		await AI.loadImage(file, { metadata })
		const baseAdjustments = { ...defaultAdjustments, ...transformedPresetAdjustments }
		aiAdjustments = await AI.calculateAutoParams(baseAdjustments, imageInfo)
	}

	// Compute Biases
	userAdjustments["temperature"] =
		userAdjustments["temperature"] ?? imageInfo.features.meta.init_temp
	userAdjustments["tint"] = userAdjustments["tint"] ?? imageInfo.features.meta.init_tint
	const aiAdjustmentsFull = {
		...defaultAdjustments,
		...transformedPresetAdjustments,
		...aiAdjustments,
	}
	const biases = computeBiases(userAdjustments, aiAdjustmentsFull, classifiedLocals)
	logAiInfo("[approveReference] old algorithm biases for", photo.id, { biases })

	// Update imageInfo
	const prevKeyID = imageInfo?.keyNeighborId
	const keyNeighborId = photo.id

	const aiAdjustmentsMap = imageInfo.aiAdjustmentsMap
	// store reference's current adjustments for later use, save time by avoiding fetching history
	userAdjustments.localAdjustments = originalLocalAdjustments
	aiAdjustmentsMap[refCurrentAdjustmentsId] = userAdjustments
	if (presetId && !hasManualLocalAdj) aiAdjustmentsMap[presetId] = aiAdjustments

	const updatedInfo: ImageInfoRecord = {
		...imageInfo,
		biases,
		aiAdjustmentsMap,
		presetAdjustmentsMap,
		keyNeighborId: keyNeighborId,
		presetId: presetId ?? null,
		nearestReferences: null,
	}

	if (isExplicit) {
		// Update clusters and ImageInfo for other members in the same cluster
		const isBlackAndWhite = biases["saturation"] === saturationBiasBW
		makeApprovedPhotoKeyNeighbor(updatedInfo, prevKeyID, isBlackAndWhite)

		// Need optical flow feature if it has localized change
		await storeFlowFeatureIfNeeded({ userAdjustments, imageInfo, file, metadata })

		updateImageInfo(updatedInfo)
	}

	return updatedInfo
}

export async function approveJpegReference(options: {
	bitmap: ImageBitmap
	photoId: PhotoId
	approvedPhotoIds: PhotoId[]
	preset?: PresetItem
	adjustments?: Partial<Adjustments>
	isPolarrAdjustments?: boolean
}) {
	const { bitmap, photoId, approvedPhotoIds, preset, adjustments, isPolarrAdjustments } = options
	const style = store.get(currentStyleAtom)

	const imageInfo = await AI.extractImageInfoFromRefJpeg(bitmap, photoId)
	let presetId = preset?.id ?? null
	let presetAdjustments: Partial<Adjustments> | undefined = preset?.adjustments
		? { ...preset.adjustments }
		: undefined
	let presetIntensity = 0

	if (
		adjustments &&
		getUseLRParamsForJpegReference() &&
		!isParamsEmpty(adjustments) &&
		(!presetAdjustments || !isMainParamsEqual(adjustments, presetAdjustments))
	) {
		const defaultAdjustments = getDefaultAdjustments()
		defaultAdjustments.colorProfile = null
		const res = getPresetFromUserAdjustments({
			userAdjustments: { ...defaultAdjustments, ...adjustments },
			defaultAdjustments,
			approvedPhotoIds,
			style,
			presetLocalAdjustments: [],
			isLRParams: isPolarrAdjustments == false,
		})
		presetId = res.presetId
		presetAdjustments = res.presetAdjustments
		presetIntensity = res.presetIntensity
	}
	delete presetAdjustments?.localAdjustments

	const presetAdjustmentsMap = imageInfo.presetAdjustmentsMap ?? {}
	if (presetAdjustments && presetId && style?.presets[presetId] == undefined) {
		presetAdjustmentsMap[presetId] = { ...presetAdjustments }
	}

	const biases: Partial<BiasType> = {}
	const aiAdjustmentsMap = imageInfo.aiAdjustmentsMap ?? {}

	const updatedInfo: ImageInfoRecord = {
		...imageInfo,
		biases,
		keyNeighborId: photoId,
		presetId,
		nearestReferences: null,
		aiAdjustmentsMap,
		presetAdjustmentsMap,
	}
	if (presetIntensity > 0 && updatedInfo.colorMatchReference) {
		updatedInfo.colorMatchReference.presetIntensity = presetIntensity
	}

	updateImageInfo(updatedInfo)

	logAiInfo("Approving jpeg reference:", photoId, {
		presetId,
		updatedInfo,
		adjustments,
		isPolarrAdjustments,
	})

	return updatedInfo
}

async function storeFlowFeatureIfNeeded({
	userAdjustments,
	imageInfo,
	file,
	metadata,
}: {
	userAdjustments: Adjustments
	imageInfo: ImageInfoRecord
	file: File
	metadata: PhotoMetadataRecord | undefined
}) {
	const hasLocalChanges = hasLocalizedChanges(
		userAdjustments,
		imageInfo,
		store.get(autoStraightenAtom),
	)
	if (hasLocalChanges) {
		const featureExists = await flowFeatureExists(imageInfo.imageId)
		if (!featureExists) {
			await AI.loadImage(file, { metadata })
			const flowFeature = await AI.extractOpticalFlowFeature(imageInfo)
			await storeFlowFeature(flowFeature)
		}
	}
}

export function getPresetFromUserAdjustments(input: {
	userAdjustments: Adjustments
	defaultAdjustments: Adjustments
	approvedPhotoIds: PhotoId[]
	style: StyleInfoRecord | null
	presetLocalAdjustments: MaskGroup[]
	isLRParams?: boolean
	appliedPresetId?: string
}) {
	const {
		userAdjustments,
		defaultAdjustments,
		approvedPhotoIds,
		style,
		presetLocalAdjustments,
		isLRParams,
		appliedPresetId,
	} = input
	const derivedPreset = derivePresetWithUserAdjustmentsForAI({
		userAdjustments,
		presetLocalAdjustments,
		defaultAdjustments,
		isLRParams,
	})
	const derivedPresetAdjustments = derivedPreset.adjustments
	let presetId: string = derivedPreset.id

	// First retrieve existing preset if adjustments are the same
	const retrievedPresetId = retrieveExistingPresetId({
		appliedPresetId,
		derivedPresetAdjustments,
		approvedPhotoIds,
		style,
	})
	if (!retrievedPresetId) {
		console.log("Derived preset:", derivedPreset.id)
	} else {
		console.log("Previous preset retrieved:", retrievedPresetId)
		presetId = retrievedPresetId
	}

	const presetIntensity = getPresetIntensity(derivedPresetAdjustments)
	return { presetId, presetAdjustments: derivedPresetAdjustments, presetIntensity }
}

// The more changes to the color the preset has (especially for the skin tones), the higher the intensity and lower the score
function getPresetIntensity(adjustments: Partial<Adjustments>) {
	let diff = 0
	let hslDiff = 0
	for (const hue of ["Red", "Orange", "Yellow", "Green", "Aqua", "Blue", "Purple", "Magenta"]) {
		const saturation = adjustments[`saturation${hue}`] ?? 0
		const luminance = adjustments[`luminance${hue}`] ?? 0
		const hueChange = adjustments[`hue${hue}`] ?? 0
		hslDiff += Math.abs(saturation) + Math.abs(luminance) + Math.abs(hueChange) * 0.3
	}
	diff += hslDiff / 4

	// Add more weight to skin tones
	const skinDiff =
		Math.abs(adjustments.hueOrange ?? 0) +
		Math.abs(adjustments.saturationOrange ?? 0) +
		Math.abs(adjustments.luminanceOrange ?? 0)
	diff += skinDiff

	diff += Math.abs(adjustments.saturation ?? 0)
	diff += Math.abs(adjustments.vibrance ?? 0)

	let calibrationDiff = 0
	calibrationDiff += Math.abs(adjustments.calibrationRedHue ?? 0)
	calibrationDiff += Math.abs(adjustments.calibrationRedSat ?? 0)
	calibrationDiff += Math.abs(adjustments.calibrationGreenHue ?? 0)
	calibrationDiff += Math.abs(adjustments.calibrationGreenSat ?? 0)
	calibrationDiff += Math.abs(adjustments.calibrationBlueHue ?? 0)
	calibrationDiff += Math.abs(adjustments.calibrationBlueSat ?? 0)
	diff += calibrationDiff / 6

	let colorGradingDiff = 0
	colorGradingDiff += Math.abs(adjustments.gradingBalance ?? 0)
	colorGradingDiff += Math.abs((adjustments.gradingBlending ?? 0.5) - 0.5)
	colorGradingDiff += Math.abs(adjustments.gradingShadowsSaturation ?? 0)
	colorGradingDiff += Math.abs(adjustments.gradingShadowsLuminance ?? 0) * 0.5
	colorGradingDiff += Math.abs(adjustments.gradingMidtonesSaturation ?? 0)
	colorGradingDiff += Math.abs(adjustments.gradingMidtonesLuminance ?? 0) * 0.5
	colorGradingDiff += Math.abs(adjustments.gradingHighlightsSaturation ?? 0)
	colorGradingDiff += Math.abs(adjustments.gradingHighlightsLuminance ?? 0) * 0.5
	colorGradingDiff += Math.abs(adjustments.gradingGlobalSaturation ?? 0)
	colorGradingDiff += Math.abs(adjustments.gradingGlobalLuminance ?? 0) * 0.5

	diff += colorGradingDiff / 4

	logAiInfo("Preset intensity:", {
		adjustments,
		diff,
		hslDiff,
		skinDiff,
		calibrationDiff,
		colorGradingDiff,
	})

	return diff
}

function retrieveExistingPresetId(options: {
	appliedPresetId?: string
	derivedPresetAdjustments: Partial<Adjustments>
	approvedPhotoIds: PhotoId[]
	style: StyleInfoRecord | null
}): string | null {
	const { appliedPresetId, derivedPresetAdjustments, approvedPhotoIds, style } = options

	const defaultAdjustments = getDefaultAdjustments()
	defaultAdjustments.colorProfile = null

	const presetMap = style ? { ...style.presets } : {}
	for (const photoId of approvedPhotoIds) {
		const refInfo = getImageInfo(photoId)
		const existingID = refInfo?.presetId
		if (existingID && !presetMap[existingID]) {
			const presetAdjustments = refInfo.presetAdjustmentsMap
				? refInfo.presetAdjustmentsMap[existingID]
				: undefined
			if (presetAdjustments) presetMap[existingID] = presetAdjustments
		}
	}

	const derivedPresetAdjustmentsFull = {
		...defaultAdjustments,
		...derivedPresetAdjustments,
	}

	if (appliedPresetId) {
		const adjustments = presetMap[appliedPresetId]
		if (adjustments) {
			if (isPresetAlmostEqual(adjustments, derivedPresetAdjustmentsFull, true)) {
				console.log(
					"[retrieveExistingPresetId] Applied preset almost equal:",
					appliedPresetId,
				)
				return appliedPresetId
			}
		}
	}

	for (const [id, adjustments] of Object.entries(presetMap)) {
		if (id === appliedPresetId) continue // already checked above
		if (isPresetAlmostEqual(adjustments, derivedPresetAdjustmentsFull)) {
			return id
		}
	}

	return null
}

function computeBiases(
	userAdjustments: Adjustments,
	aiAdjustments: Partial<Adjustments>,
	classifiedLocals: ClassifiedLocalAdjustments | undefined,
) {
	const biases: Partial<BiasType> = {}

	Object.keys(userAdjustments).forEach((key) => {
		// if (ignoreBiasKeys.includes(key)) return // we want crop/straighten to trigger updates

		const userAdjustment = userAdjustments[key]
		const aiAdjustment = aiAdjustments[key]

		if (key === "colorProfile") {
			// This is of type number enum
			if (!equal(userAdjustment, aiAdjustment)) {
				biases[key] = userAdjustment
			}
		} else if (typeof userAdjustment === "number" && typeof aiAdjustment === "number") {
			if (key === "temperature") {
				biases[key] = getTemperatureBias(userAdjustment, aiAdjustment)
			} else {
				biases[key] = userAdjustment - aiAdjustment
			}
		} else if (
			key.includes("curves") ||
			key === "pointColors" ||
			key === "crop" ||
			key === "healingMasks"
		) {
			// curve and pointColors: just use the same value as the nearest reference
			// crop: store crop in biases so that crop updates can also trigger reprocessing
			if (userAdjustment && !equal(userAdjustment, aiAdjustment)) {
				biases[key] = userAdjustment
			}
		} else if (key === "localAdjustments" && userAdjustment && classifiedLocals) {
			// The localAdjustments biases were designed to be difference between user-modified-local and preset-local
			// But now localBiases are always 0 because we treat a localAdj-modified preset as a new preset, so classifiedLocals.modifiedPreset is []
			// Main reason is we don't want to apply any uncertain localBiases after auto; it's more robust if the auto is computed on top of known localAdjustments.
			// Relevant code can be deleted in the future if this approach works without problem.
			const localBiases = getLocalAdjustmentsBiases(classifiedLocals, aiAdjustment)
			if (localBiases.length > 0) {
				biases[key] = localBiases
			}

			// manual means localAdjs that're not part of the preset - used in keyAdjustments
			if (classifiedLocals.manual.length > 0) {
				biases.localAdjustmentsManual = classifiedLocals.manual
			}
		}
	})

	// special saturation bias to label B&W ref photo
	if (
		userAdjustments.saturation < -0.8 ||
		userAdjustments.vibrance < -0.9 ||
		userAdjustments.saturation + userAdjustments.vibrance < -1.4 ||
		biases.colorProfile === ColorProfileName.Monochrome ||
		biases.colorProfile === ColorProfileName.StandardMonochrome
	) {
		biases["saturation"] = saturationBiasBW
	}

	return biases
}

export function reprocess(photos: PhotoRecord[], triggerSource?: string | null) {
	if (store.get(isCalibrationAtom)) {
		return
	}

	const style = store.get(currentStyleAtom)

	if (!style) {
		return
	}

	// Trigger reprocessing regardless of user made changes or not
	if (photos.length > 0) {
		const release = holdAIStyleScheduling()
		releasePendingReprocess?.()
		releasePendingReprocess = release
		updateAIProfileDebounced(style, triggerSource)
	}

	// This check is wrong if user first made manual adjustments, mark-as-ref, then go back to AI-profile adjustments in history, mark-as-ref
	//if (!isEqual(adjustments, info?.aiProfileAdjustments ?? {}))

	// Right way to check:
	// for (const photo of photos) {
	// 	const hasUserChanges = await hasManualChanges(photo)

	// 	if (hasUserChanges) {
	// 		//Update profile and reapply to non-approved files
	// 		updateAIProfileDebounced(style)
	// 		break
	// 	}
	// }
}

let releasePendingReprocess: (() => void) | undefined
const updateAIProfileDebounced = debounce(
	async (style: StyleInfoRecord, triggerSource?: string | null) => {
		const release = releasePendingReprocess
		releasePendingReprocess = undefined
		try {
			await updateAIStyle(style, triggerSource)
		} finally {
			release?.()
		}
	},
	100,
)
